/**
 * @file ApprovalQueue.jsx
 * @module ui/src/components/admin/ApprovalQueue
 * @description
 *   Renders the CA-2 work queue across actionable, SoD-blocked, and approval-limbo
 *   visibility states. Queue items and approval eligibility are entirely server-authored.
 *
 * @version 1.1.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S777
 *
 * @architecture
 *   Locked decisions : #2, #16
 *   Temporal invariants : INV-1, INV-3
 *   Design principles : DP-1, DP-5, DP-11, DP-13, DP-14
 *   Cross-cutting contracts : XC-3, XC-7
 *
 * @domain-rules
 *   - Queue items and approval eligibility are entirely server-authored via ep 21 GET /api/v1/approvals/queue.
 *   - approvalLimboWarning is a server field per item — never computed client-side.
 *   - SoD eligibility is expressed via callerEligibility{eligible, blockedReason} per item — never computed client-side.
 *   - Economic effective date is labeled separately from submitted/approval audit timing.
 *   - Approval scope coverage and SoD eligibility remain server decisions; this queue never computes them.
 *
 * @wiring-s777
 *   ep 21 GET /api/v1/approvals/queue — WIRED S777
 *   Response: {queueItems[], pagination{hasMore, nextCursor}, totalCount, eligibleCount}
 *   Per item: {itemId, itemType, planId, planCode, planName, planStatus, amendmentId,
 *              successorPlanId, changeSetId, submittedBy, submittedByName, submittedAt,
 *              fiscalPeriodId, approvalLimboWarning, callerEligibility{eligible, blockedReason}}
 *   OI-S774-QUEUE-TIER-FIELDS closed: tier fields removed — not in router response.
 *   OI-S774-QUEUE-PARAMS-IGNORED closed: unsupported query params removed.
 *
 * @migration-flags
 *   - plan.effective_from / plan.effective_to — pending m_tc_01 (Neon FROZEN)
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group CA-2 batch delivery
 *   1.1.0  S777  Wired to ep 21 router response. Removed tier/scope/param system.
 *                Fixed items[]→queueItems[]. Removed AI ref.
 *   1.2.0  S895  draw_ledger_init item type added. DrawLedgerApprovalReview mounted.
 *                ITEM_TYPE_LABELS extended. Import added.
 *   1.3.0  S900  amendmentId passed to PlanApprovalReview for plan_approval items.
 *                Fixes OI-S900-AMENDMENT-APPROVE-WRONG-ENDPOINT — amendment targets
 *                now route to EP14 instead of EP9. AD-66 + OD-18.
 */

import PropTypes from 'prop-types';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { apiGet } from '../../api/client.js';  // OI-S900: replaces raw fetch in refresh handlers
import ApprovalEligibilityGate from '../shared/ApprovalEligibilityGate';
import AmendmentApprovalReview from './AmendmentApprovalReview';
import DrawLedgerApprovalReview from './DrawLedgerApprovalReview';
import PlanApprovalReview from './PlanApprovalReview';
import ReturnForChangesDrawer from './ReturnForChangesDrawer';

/**
 * @typedef {Object} ApprovalQueueItem
 * @property {string} itemId
 * @property {string} itemType - 'plan_approval' | 'amendment_approval' | 'change_set_approval'
 * @property {number} planId
 * @property {string} planCode
 * @property {string} planName
 * @property {string} planStatus
 * @property {string|null} amendmentId
 * @property {number|null} successorPlanId
 * @property {string|null} changeSetId
 * @property {string|null} submittedBy
 * @property {string|null} submittedByName
 * @property {string|null} submittedAt
 * @property {number|null} fiscalPeriodId
 * @property {boolean} approvalLimboWarning
 * @property {{eligible: boolean, blockedReason: string|null}} callerEligibility
 */

/**
 * @typedef {Object} ApprovalQueueProps
 * @property {Object} currentUser
 */

const LIMBO_COPY = "No eligible approver exists for this plan's scope.";
const SOD_COPY = 'You submitted this plan. Another approver is required.';

const ITEM_TYPE_LABELS = {
  plan_approval:       'New Plan',
  amendment_approval:  'Amendment',
  change_set_approval: 'Change Set',
  // S895: draw ledger initialization CA-2 approval (T3-5)
  // item_id = authorization_id (uuid). No planId — uses draw_plan name/code.
  draw_ledger_init:    'Draw Ledger Init',
};

/**
 * Adapts ep 21 callerEligibility shape → ApprovalEligibilityGate prop shape.
 * ep 21 router returns:  { eligible: boolean, blockedReason: 'sod_violation'|null }
 * Gate component reads:  { canApprove: boolean, reasonCode: string|null }
 */
function ep21ToGateEligibility(callerEligibility) {
  const eligible      = callerEligibility?.eligible      ?? false;
  const blockedReason = callerEligibility?.blockedReason ?? null;
  const reasonCode =
    blockedReason === 'sod_violation'  ? 'self_submitted'  :
    blockedReason === 'scope_mismatch' ? 'scope_mismatch'  :
    blockedReason ?? null;
  return { canApprove: eligible, reasonCode };
}

const sortItems = (items, sortKey) => {
  const rows = [...items];

  return rows.sort((a, b) => {
    if (sortKey === 'submitted_at_asc') {
      return String(a.submittedAt || '').localeCompare(String(b.submittedAt || ''));
    }

    if (sortKey === 'plan_name_asc') {
      return String(a.planName || '').localeCompare(String(b.planName || ''));
    }

    return String(b.submittedAt || '').localeCompare(String(a.submittedAt || ''));
  });
};

/**
 * CA-2 approval queue — wired to ep 21 GET /api/v1/approvals/queue.
 *
 * @param {ApprovalQueueProps} props
 * @returns {JSX.Element}
 */
function ApprovalQueue() {
  // WHY: sort selection changes review ordering only and never eligibility or governance state.
  const [sort, setSort] = useState('submitted_at_desc');

  // WHY: selected item identity records the review handoff choice without deriving or mutating governance outcome.
  const [selectedItemId, setSelectedItemId] = useState(null);

  // WHY: returnDrawerOpen controls C-29 ReturnForChangesDrawer — separate from selectedItemId so the
  // drawer can be closed without deselecting the item under review.
  const [returnDrawerOpen, setReturnDrawerOpen] = useState(false);

  // WHY: queue rows, counts, eligibility, and limbo warnings are all authoritative server data from ep 21.
  const [queueData, setQueueData] = useState(null);

  // WHY: loading state distinguishes an in-flight queue query from a legitimately empty approval workload.
  const [loading, setLoading] = useState(false);

  // WHY: queue retrieval failures remain visible instead of falling back to stale or locally inferred state.
  const [error, setError] = useState('');

  // WHY: the queue is fetched on mount. ep 21 returns the full queue — no query params required.
  useEffect(() => {
    let active = true;

    const loadQueue = async () => {
      setLoading(true);
      setError('');

      try {
        // WHY: ep 21 is the authority for visible work, eligibility payloads, limbo detection, and counts.
        // No query params — router returns the full tenant-scoped queue in one call.
        const response = await fetch('/api/v1/approvals/queue', {
          method: 'GET',
          credentials: 'include',  // OI-S900: was missing — cookie never sent
          headers: {
            Accept: 'application/json',
          },
        });

        // WHY: a failed queue read cannot safely be replaced by client computation.
        if (!response.ok) {
          throw new Error('Unable to load the approval queue.');
        }

        const payload = await response.json();

        // WHY: stale asynchronous responses must not replace data after component unmounts.
        if (active) {
          setQueueData(payload);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError.message || 'Unable to load the approval queue.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadQueue();

    return () => {
      active = false;
    };
  }, []);

  // WHY: sort changes presentation order only and does not trigger a server re-fetch.
  const handleSortChange = (event) => {
    setSort(event.target.value);
  };

  // WHY: review selection mounts the appropriate review component without altering governance state.
  const handleReview = (item) => {
    setSelectedItemId(item.itemId);
    setReturnDrawerOpen(false);
  };

  // WHY: onApprove refreshes the queue after a confirmed governance transition.
  const handleApproveSuccess = () => {
    setSelectedItemId(null);
    setReturnDrawerOpen(false);
    setQueueData(null);
    // OI-S900: was raw fetch() — missing credentials, errors silently swallowed.
    apiGet('/api/v1/approvals/queue')
      .then((payload) => { if (payload) setQueueData(payload); })
      .catch(() => {});
  };

  // WHY: onReturn opens C-29 ReturnForChangesDrawer without discarding the selected item.
  const handleOpenReturnDrawer = () => setReturnDrawerOpen(true);

  // WHY: after confirmed return (ep 10/15 HTTP 200) refresh queue and close drawer.
  const handleReturnSuccess = () => {
    setReturnDrawerOpen(false);
    setSelectedItemId(null);
    setQueueData(null);
    // OI-S900: was raw fetch() — missing credentials, errors silently swallowed.
    apiGet('/api/v1/approvals/queue')
      .then((payload) => { if (payload) setQueueData(payload); })
      .catch(() => {});
  };

  // WHY: cancel closes the drawer without a server mutation.
  const handleCancelReturn = () => setReturnDrawerOpen(false);

  // WHY: sorted view over the server-authoritative queueItems array.
  const visibleItems = useMemo(() => {
    // WHY: ep 21 returns queueItems[] — not items[]. Router-confirmed S777.
    const items = queueData?.queueItems || [];
    return sortItems(items, sort);
  }, [queueData, sort]);

  // WHY: selectedItem derived after visibleItems to avoid temporal dead zone.
  const selectedItem = visibleItems.find((i) => i.itemId === selectedItemId) || null;

  // WHY: initial loading uses skeletons so an empty visual surface is not interpreted as zero pending approvals.
  if (loading && !queueData) {
    return (
      <section className="modifier-manager-card" aria-busy="true">
        <header className="modifier-manager-header">
          <h2>Approval queue</h2>
        </header>
        <div className="modifier-manager-body">
          <div className="skeleton" style={{ height: '72px' }} />
          <div className="skeleton" style={{ height: '72px' }} />
          <div className="skeleton" style={{ height: '72px' }} />
        </div>
      </section>
    );
  }

  // WHY: initial queue failure cannot be substituted with client-derived eligibility.
  if (error && !queueData) {
    return (
      <div className="modifier-manager-error-state" role="alert">
        {error}
      </div>
    );
  }

  // WHY: ep 21 router fields — confirmed S777 from approvals_router.py.
  const totalCount = queueData?.totalCount ?? 0;
  const eligibleCount = queueData?.eligibleCount ?? 0;

  return (
    <>
    <section className="modifier-manager-card">
      <header className="modifier-manager-header">
        <div>
          <h2>Approval queue</h2>
          <p style={{ color: 'rgb(var(--color-text-muted))', marginBlockEnd: 0 }}>
            {eligibleCount} eligible for you · {totalCount} total
          </p>
        </div>
      </header>

      <div className="modifier-manager-body">
        {/* WHY: queue fetch errors after an existing payload remain visible without discarding the last authoritative queue state. */}
        {error && (
          <div className="modifier-manager-inline-error" role="alert">
            {error}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            justifyContent: 'space-between',
            alignItems: 'end',
          }}
        >
          <label>
            <span style={{ display: 'block', color: 'rgb(var(--color-text-muted))' }}>
              Sort
            </span>

            <select value={sort} onChange={handleSortChange}>
              <option value="submitted_at_desc">Newest first</option>
              <option value="submitted_at_asc">Oldest first</option>
              <option value="plan_name_asc">Plan name A–Z</option>
            </select>
          </label>
        </div>

        <p style={{ color: 'rgb(var(--color-text-muted))' }}>
          Approval eligibility is determined by the server for each document.
        </p>

        {/* WHY: a genuinely empty queue is explicit and remains different from a loading or failed request. */}
        {visibleItems.length === 0 ? (
          <div className="modifier-manager-empty-state">
            No approval items in queue.
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {visibleItems.map((item) => {
              const isSelected = selectedItemId === item.itemId;
              // WHY: approvalLimboWarning is a server field — never computed client-side.
              const isLimbo = item.approvalLimboWarning === true;
              // WHY: callerEligibility.eligible is a server field — never computed client-side.
              const isEligible = item.callerEligibility?.eligible === true;
              const blockedReason = item.callerEligibility?.blockedReason || null;
              const isSodBlocked = blockedReason === 'sod_violation';

              return (
                <article
                  key={item.itemId}
                  className="modifier-manager-card"
                  aria-current={isSelected ? 'true' : undefined}
                  style={
                    isSelected
                      ? { background: 'rgb(var(--color-selected-bg))' }
                      : undefined
                  }
                >
                  <header className="modifier-manager-header">
                    <div>
                      <h3 style={{ marginBlockEnd: '4px' }}>
                        {item.planName}
                      </h3>

                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                          alignItems: 'center',
                        }}
                      >
                        <span>{item.planCode}</span>

                        <span className="modifier-manager-badge modifier-manager-badge--readonly">
                          {/* WHY: itemType from router — 'plan_approval' | 'amendment_approval' | 'change_set_approval' */}
                          {ITEM_TYPE_LABELS[item.itemType] || item.itemType || 'Review'}
                        </span>

                        <span className="modifier-manager-badge modifier-manager-badge--readonly">
                          {item.planStatus}
                        </span>
                      </div>
                    </div>
                  </header>

                  <div className="modifier-manager-body">
                    <p>
                      Submitted by{' '}
                      {item.submittedByName || item.submittedBy || 'unknown'}{' '}
                      on {item.submittedAt || 'unknown date'}
                    </p>

                    {/* WHY: amendmentId present only for amendment_approval and change_set_approval items. */}
                    {item.amendmentId && (
                      <p>
                        <strong>Amendment:</strong> {item.amendmentId}
                      </p>
                    )}

                    {/* WHY: changeSetId present only for change_set_approval items. */}
                    {item.changeSetId && (
                      <p>
                        <strong>Change Set:</strong> {item.changeSetId}
                      </p>
                    )}

                    {item.fiscalPeriodId && (
                      <p>
                        <strong>Fiscal period:</strong> {item.fiscalPeriodId}
                      </p>
                    )}

                    <div>
                      <strong>Eligibility status:</strong>

                      {/* WHY: ep21ToGateEligibility adapts {eligible,blockedReason} → gate {canApprove,reasonCode}. */}
                      <ApprovalEligibilityGate
                        eligibility={ep21ToGateEligibility(item.callerEligibility)}
                        blockedMessage={
                          isSodBlocked
                            ? SOD_COPY
                            : 'You are not eligible to approve this item.'
                        }
                      >
                        <span className="modifier-manager-badge modifier-manager-badge--complete">
                          Eligible to act
                        </span>
                      </ApprovalEligibilityGate>
                    </div>

                    {/* WHY: approvalLimboWarning is a server field — rendered verbatim when true. */}
                    {isLimbo && (
                      <div className="modifier-manager-warning">
                        {LIMBO_COPY}
                      </div>
                    )}

                    {/* WHY: SoD block is surfaced with the exact explanation so CA-2 knows another approver is needed. */}
                    {isSodBlocked && (
                      <div className="modifier-manager-info">
                        {SOD_COPY}
                      </div>
                    )}

                    <footer className="modifier-manager-footer">
                      {/* WHY: only eligible items expose the review action; ApprovalEligibilityGate still gates with server eligibility. */}
                      {isEligible && (
                        <ApprovalEligibilityGate
                          eligibility={ep21ToGateEligibility(item.callerEligibility)}
                          blockedMessage="You are not eligible to approve this item."
                        >
                          <button
                            type="button"
                            className="primary-button"
                            onClick={() => handleReview(item)}
                          >
                            Review
                          </button>
                        </ApprovalEligibilityGate>
                      )}

                      {/* WHY: SoD-blocked items receive a disabled action so CA-2 knows another approver is required. */}
                      {isSodBlocked && (
                        <button
                          type="button"
                          className="secondary-button"
                          disabled
                          title={SOD_COPY}
                        >
                          Another approver required
                        </button>
                      )}

                      {/* WHY: limbo items have no approval action because the server reports no eligible approver exists. */}
                      {isLimbo && (
                        <span className="modifier-manager-badge modifier-manager-badge--incomplete">
                          Approval limbo
                        </span>
                      )}
                    </footer>

                    {/* WHY: selectedItemId records the review handoff target. */}
                    {isSelected && (
                      <div className="modifier-manager-info" role="status">
                        Review selected: {ITEM_TYPE_LABELS[item.itemType] || 'Review'}.
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>

      {selectedItem && selectedItem.itemType === 'plan_approval' && (
        <PlanApprovalReview
          planId={selectedItem.planId}
          amendmentId={selectedItem.amendmentId ?? undefined}
          currentUser={{}}
          onApprove={handleApproveSuccess}
          onReturn={handleOpenReturnDrawer}
        />
      )}

      {selectedItem && selectedItem.itemType === 'amendment_approval' && selectedItem.amendmentId && (
        <AmendmentApprovalReview
          planId={selectedItem.planId}
          amendmentId={selectedItem.amendmentId}
          currentUser={{}}
          onApprove={handleApproveSuccess}
          onReturn={handleOpenReturnDrawer}
        />
      )}

      {/* S895: draw_ledger_init — CA-2 approval of draw ledger opening balance.
           item.itemId = authorization_id. No planId (draw plan uses planCode/planName).
           onDismiss deselects the item — no return/reject path in v1.
           WHY: draw_ledger_init has no ReturnForChangesDrawer path — the authorization
           can only be approved or left pending. Revocation is a separate future surface. */}
      {selectedItem && selectedItem.itemType === 'draw_ledger_init' && (
        <DrawLedgerApprovalReview
          item={selectedItem}
          onApprove={handleApproveSuccess}
          onDismiss={() => setSelectedItemId(null)}
        />
      )}

      {returnDrawerOpen && selectedItem && (
        <ReturnForChangesDrawer
          planId={selectedItem.planId}
          amendmentId={selectedItem.amendmentId || null}
          onReturn={handleReturnSuccess}
          onCancel={handleCancelReturn}
        />
      )}
    </>
  );
}

ApprovalQueue.propTypes = {
  currentUser: PropTypes.object.isRequired,
};

export default ApprovalQueue;

/**
 * @example
 * <ApprovalQueue
 *   currentUser={currentUser}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK — S777
 * - ep 21 GET /api/v1/approvals/queue wired correctly.
 * - queueItems[] (not items[]) — OI-S774-QUEUE-TIER-FIELDS closed.
 * - No query params sent — OI-S774-QUEUE-PARAMS-IGNORED closed.
 * - approvalLimboWarning: server field per item — never computed client-side.
 * - callerEligibility{eligible, blockedReason}: server field per item — never computed client-side.
 * - itemType: server field ('plan_approval'|'amendment_approval'|'change_set_approval') — not 'reviewType'.
 * - submittedByName: server field — resolved server-side via user_account JOIN in router.
 * - Limbo renders the locked copy verbatim: "No eligible approver exists for this plan's scope."
 * - SoD renders the exact explanation: "You submitted this plan. Another approver is required."
 * - DP-11 / DP-13: scope coverage and SoD are never computed client-side.
 * - XC-7 / DP-14: eligible, SoD-blocked, and limbo states are all expressed in text.
 * - DP-8 audit: no sub-document or per-modifier CA-2 approval framing exists.
 */

/**
 * MIGRATION FLAGS — S777
 * - plan.effective_from / plan.effective_to — pending m_tc_01 (Neon FROZEN).
 * - GET /api/v1/approvals/queue — WIRED S777. Router confirmed: no pagination params,
 *   full queue returned. pagination.hasMore always False in current implementation.
 */

/**
 * ACCESSIBILITY NOTES
 * - Queue state is never represented by color alone.
 * - Review cards use article semantics and selected cards expose aria-current.
 * - SoD disabled action includes both visible explanatory copy and a title attribute.
 * - Limbo warning is visible text and not badge color alone.
 * - ApprovalEligibilityGate supplies server-authored eligibility explanation.
 * - Queue errors use role="alert"; review selection confirmation uses role="status".
 */
